import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { QueryState } from "@/shared/ui/QueryState";
import { Button, Card, confirm, toast } from "@devhop/ui";
import { Building, FileDown, Save, UserCheck, Users, Zap } from "lucide-react";
import type React from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import type { ReactFlowInstance } from "reactflow";
import {
  Background,
  Controls,
  MiniMap,
  Panel,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
} from "reactflow";
import "reactflow/dist/style.css";
import type { PositionWithDetails } from "../../domain/types";
import { useOrgChartOverview, useUpdateNodesPosition } from "../api/queries";
import { OrgChartNodeComponent } from "../ui/OrgChartNode";
import { OrgChartSidebar } from "../ui/OrgChartSidebar";

// Define node types for React Flow
const nodeTypes = {
  position: OrgChartNodeComponent,
};

export function OrgChartOverviewPage() {
  const { data, isLoading, error } = useOrgChartOverview({});
  const updateNodesPositionMutation = useUpdateNodesPosition();

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selectedPosition, setSelectedPosition] =
    useState<PositionWithDetails | null>(null);

  // Store node positions that have been changed locally (not yet saved)
  const [unsavedPositions, setUnsavedPositions] = useState<
    Map<string, { x: number; y: number }>
  >(new Map());

  // Store original positions from database for comparison
  const originalPositionsRef = useRef<Map<string, { x: number; y: number }>>(
    new Map(),
  );

  // Track if save is in progress to prevent concurrent saves
  const isSavingRef = useRef(false);
  const reactFlowRef = useRef<ReactFlowInstance | null>(null);

  // Handle node click to show details in sidebar
  const onNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedPosition(node.data.position);
  }, []);

  // Handle node drag stop - store new position in local state
  const onNodeDragStop = useCallback((_event: React.MouseEvent, node: Node) => {
    const originalPos = originalPositionsRef.current.get(node.id);
    const newPos = { x: node.position.x, y: node.position.y };

    // Only store if position actually changed
    if (
      !originalPos ||
      originalPos.x !== newPos.x ||
      originalPos.y !== newPos.y
    ) {
      setUnsavedPositions((prev) => {
        const updated = new Map(prev);
        updated.set(node.id, newPos);
        return updated;
      });
    }
  }, []);

  // Layout nodes in a hierarchical structure
  const layoutNodes = useCallback(
    (rawNodes: Node[]) => {
      const levelMap = new Map<number, Node[]>();
      const positionedNodes: Node[] = [];
      const processedNodeIds = new Set<string>();

      // Store original positions from database
      const originalPositions = new Map<string, { x: number; y: number }>();

      // First pass: use saved positions from database or unsaved local positions
      rawNodes.forEach((node) => {
        const nodeId = node.id;

        // Prevent duplicate nodes
        if (processedNodeIds.has(nodeId)) {
          return;
        }
        processedNodeIds.add(nodeId);

        const savedPos = node.position;
        const unsavedPos = unsavedPositions.get(nodeId);

        // Check if node has saved position in database (chartX/chartY are not null)
        const chartX = node.data.position.chartX;
        const chartY = node.data.position.chartY;
        const hasSavedPosition =
          chartX !== null &&
          chartX !== undefined &&
          chartY !== null &&
          chartY !== undefined;

        // Use unsaved position if available, otherwise use saved position from database
        let finalPosition: { x: number; y: number };
        if (unsavedPos) {
          finalPosition = unsavedPos;
          // Store original position from database if not already stored
          if (!originalPositions.has(nodeId)) {
            originalPositions.set(nodeId, savedPos);
          }
        } else if (hasSavedPosition) {
          // Position was saved in database, use it directly
          finalPosition = savedPos;
          originalPositions.set(nodeId, savedPos);
        } else {
          // No saved position, will be laid out automatically
          const level = node.data.level;
          if (!levelMap.has(level)) {
            levelMap.set(level, []);
          }
          const levelNodes = levelMap.get(level);
          if (levelNodes) {
            levelNodes.push(node);
          }
          // Store original position (default layout)
          originalPositions.set(nodeId, savedPos);
          return; // Skip adding to positionedNodes for now, will be added in layout pass
        }

        positionedNodes.push({
          ...node,
          position: finalPosition,
        });
      });

      // Update original positions ref
      originalPositionsRef.current = originalPositions;

      // Layout nodes that don't have saved positions
      levelMap.forEach((levelNodes, level) => {
        const nodeWidth = 160; // Card width (w-40 = 160px)
        const horizontalSpacing = 30; // Space between nodes
        const totalWidth =
          levelNodes.length * nodeWidth +
          (levelNodes.length - 1) * horizontalSpacing;
        const startX = -totalWidth / 2;

        levelNodes.forEach((node, index) => {
          // Double check to prevent duplicates
          if (!processedNodeIds.has(node.id)) {
            processedNodeIds.add(node.id);
            positionedNodes.push({
              ...node,
              position: {
                x: startX + index * (nodeWidth + horizontalSpacing),
                y: (level - 1) * 200, // Vertical spacing between levels
              },
            });
          }
        });
      });

      return positionedNodes;
    },
    [unsavedPositions],
  );

  // Update nodes and edges when data changes
  useMemo(() => {
    if (data) {
      const positionedNodes = layoutNodes(data.nodes);
      setNodes(positionedNodes);
      setEdges(data.edges);
    }
  }, [data, layoutNodes, setNodes, setEdges]);

  // Handle save button click with throttling to prevent concurrent saves
  const handleSave = useCallback(async () => {
    if (unsavedPositions.size === 0) return;

    // Prevent concurrent saves
    if (isSavingRef.current) {
      return;
    }

    const positionsToSave = Array.from(unsavedPositions.entries()).map(
      ([nodeId, pos]) => ({
        nodeId,
        x: pos.x,
        y: pos.y,
      }),
    );

    // Validate positions count to prevent excessive updates
    if (positionsToSave.length > 100) {
      toast.error("ບໍ່ສາມາດບັນທຶກໄດ້", {
        description: "ກະລຸນາບັນທຶກບໍ່ເກີນ 100 ຕຳແໜ່ງໃນເວລາດຽວ",
      });
      return;
    }

    isSavingRef.current = true;
    try {
      await toast.promise(
        updateNodesPositionMutation.mutateAsync(positionsToSave),
        {
          loading: "ກຳລັງບັນທຶກ...",
          success: "ບັນທຶກຕຳແໜ່ງສຳເລັດ",
          error: "ບັນທຶກຕຳແໜ່ງລົ້ມເຫຼວ",
        },
      );
      // Clear unsaved positions on success
      setUnsavedPositions(new Map());
    } catch (error) {
      // Error handling is done by toast.promise
      console.error("Failed to save node positions:", error);
    } finally {
      isSavingRef.current = false;
    }
  }, [unsavedPositions, updateNodesPositionMutation]);

  // Handle auto arrange button click
  const handleAutoArrange = useCallback(async () => {
    if (!data) return;

    const ok = await confirm({
      title: "ຈັດຕຳແໜ່ງອັດຕະໂນມັດ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະຈັດຕຳແໜ່ງທັງໝົດໃໝ່? ການຈັດຕຳແໜ່ງທີ່ບັນທຶກໄວ້ຈະຖືກທົດແທນ.",
      actionText: "ຈັດຕຳແໜ່ງ",
      ActionProps: {
        variant: "default",
      },
    });

    if (!ok) return;

    // Calculate new positions for all nodes based on hierarchy
    const levelMap = new Map<number, typeof data.nodes>();
    const nodeWidth = 160;
    const horizontalSpacing = 30;
    const verticalSpacing = 200;

    // Group nodes by level
    data.nodes.forEach((node) => {
      const level = node.data.level;
      if (!levelMap.has(level)) {
        levelMap.set(level, []);
      }
      const levelNodes = levelMap.get(level);
      if (levelNodes) {
        levelNodes.push(node);
      }
    });

    // Calculate positions for each level
    const newPositions = new Map<string, { x: number; y: number }>();
    levelMap.forEach((levelNodes, level) => {
      const totalWidth =
        levelNodes.length * nodeWidth +
        (levelNodes.length - 1) * horizontalSpacing;
      const startX = -totalWidth / 2;

      levelNodes.forEach((node, index) => {
        newPositions.set(node.id, {
          x: startX + index * (nodeWidth + horizontalSpacing),
          y: (level - 1) * verticalSpacing,
        });
      });
    });

    // Update nodes state immediately for visual feedback
    const updatedNodes: Node[] = data.nodes.map((node) => {
      const newPos = newPositions.get(node.id);
      if (newPos) {
        return {
          ...node,
          position: newPos,
        } as Node;
      }
      return node as Node;
    });
    setNodes(updatedNodes);

    // Save all positions
    const positionsToSave = Array.from(newPositions.entries()).map(
      ([nodeId, pos]) => ({
        nodeId,
        x: pos.x,
        y: pos.y,
      }),
    );

    try {
      await toast.promise(
        updateNodesPositionMutation.mutateAsync(positionsToSave),
        {
          loading: "ກຳລັງຈັດຕຳແໜ່ງ...",
          success: "ຈັດຕຳແໜ່ງສຳເລັດ",
          error: "ຈັດຕຳແໜ່ງລົ້ມເຫຼວ",
        },
      );
      // Clear unsaved positions on success
      setUnsavedPositions(new Map());
    } catch (error) {
      console.error("Failed to auto arrange positions:", error);
    }
  }, [data, setNodes, updateNodesPositionMutation]);

  const hasUnsavedChanges = unsavedPositions.size > 0;

  // Export / print org chart as PDF (uses browser print dialog → Save as PDF, A4 แนวนอน, ไม่ตัดการ์ด)
  const handleExportPdf = useCallback(() => {
    const instance = reactFlowRef.current;
    if (typeof instance?.fitView === "function") {
      instance.fitView({
        padding: 0,
        duration: 200,
        maxZoom: 1.2,
        minZoom: 0.2,
      });
    }
    const styleId = "org-chart-print-landscape-style";
    const cleanup = () => {
      document.body.classList.remove("org-chart-print-landscape");
      const el = document.getElementById(styleId);
      if (el) el.remove();
      window.removeEventListener("afterprint", cleanup);
    };
    document.body.classList.add("org-chart-print-landscape");
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent =
        "@media print{body.org-chart-print-landscape{page:org-chart-landscape}}" +
        "@page org-chart-landscape{margin:1cm;size:A4 landscape}" +
        "#org-chart-print-area,#org-chart-print-area .react-flow,#org-chart-print-area .react-flow__viewport,#org-chart-print-area .react-flow__renderer{overflow:visible!important}" +
        "#org-chart-print-area .react-flow__pane{overflow:visible!important}";
      document.head.appendChild(style);
    }
    window.addEventListener("afterprint", cleanup);
    setTimeout(() => {
      window.print();
    }, 250);
  }, []);

  const stats = useMemo(() => {
    if (!data) return null;
    return {
      totalEmployees: data.totalEmployees,
      totalPositions: data.totalPositions,
      totalDepartments: data.totalDepartments,
    };
  }, [data]);

  if (isLoading) {
    return <QueryState isLoading>ກຳລັງໂຫຼດຜັງອົງກອນ...</QueryState>;
  }

  if (error) {
    return (
      <QueryState isError error={error}>
        ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຜັງອົງກອນ
      </QueryState>
    );
  }

  return (
    <>
      <Header />
      <Main className="flex h-[89vh] bg-background p-0">
        {/* Main Flow Area */}
        <div
          id="org-chart-print-area"
          className="org-chart-print-area relative flex-1"
        >
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onInit={(instance) => {
              reactFlowRef.current = instance;
            }}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onNodeDragStop={onNodeDragStop}
            nodeTypes={nodeTypes}
            nodesDraggable={true}
            fitView
            className="bg-background"
          >
            <Controls className="border bg-background print:hidden" />
            <Background color="hsl(var(--muted))" gap={20} />

            {/* Stats Panel */}
            {stats && (
              <Panel position="top-left" className="space-y-2 print:hidden">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAutoArrange}
                    disabled={updateNodesPositionMutation.isPending}
                    className="gap-2"
                  >
                    <Zap className="h-4 w-4" />
                    ຈັດຕຳແໜ່ງອັດຕະໂນມັດ
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportPdf}
                    className="gap-2"
                  >
                    <FileDown className="h-4 w-4" />
                    ສົ່ງເປັນ PDF
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <Card className="border border-border bg-card p-4">
                    <div className="flex items-center space-x-2">
                      <Users className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-card-foreground text-sm">
                          ພະນັກງານ
                        </p>
                        <p className="font-bold text-card-foreground text-lg">
                          {stats.totalEmployees}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="border border-border bg-card p-4">
                    <div className="flex items-center space-x-2">
                      <UserCheck className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-card-foreground text-sm">
                          ຕໍາແໜ່ງ
                        </p>
                        <p className="font-bold text-card-foreground text-lg">
                          {stats.totalPositions}
                        </p>
                      </div>
                    </div>
                  </Card>

                  <Card className="border border-border bg-card p-4">
                    <div className="flex items-center space-x-2">
                      <Building className="h-5 w-5 text-primary" />
                      <div>
                        <p className="font-medium text-card-foreground text-sm">
                          ພະແນກ
                        </p>
                        <p className="font-bold text-card-foreground text-lg">
                          {stats.totalDepartments}
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </Panel>
            )}

            <MiniMap
              nodeColor="hsl(var(--muted))"
              maskColor="hsl(var(--background)/0.8)"
              className="border bg-background print:hidden"
            />
          </ReactFlow>

          {/* Floating Save Button */}
          {hasUnsavedChanges && (
            <div className="absolute inset-x-0 bottom-6 z-10 flex justify-center print:hidden">
              <Button
                onClick={handleSave}
                disabled={updateNodesPositionMutation.isPending}
                isLoading={updateNodesPositionMutation.isPending}
                size="lg"
                className="shadow-lg"
              >
                <Save className="mr-2 h-4 w-4" />
                ບັນທຶກຕຳແໜ່ງ
              </Button>
            </div>
          )}
        </div>

        {/* Sidebar for details */}
        <OrgChartSidebar
          selectedPosition={selectedPosition}
          onClose={() => setSelectedPosition(null)}
        />
      </Main>
    </>
  );
}
